'use client';

import { useState, useRef, useEffect } from 'react';

type Messaggio = {
  ruolo: 'user' | 'assistant'; 
  testo: string;
};

const MAX_MESSAGGI = 30;

// Guardia a livello di MODULO (non per-istanza): condivisa da tutti i montaggi
// del widget nello stesso tab. ConditionalAppShell può montare/smontare/rimontare
// il widget in rapida successione durante idratazione e transizioni di rotta;
// ogni montaggio ha state e ref freschi, quindi un ref interno non basta a
// impedire fetch concorrenti. Questo flag, settato SINCRONAMENTE in cima alla
// callback del timer prima di qualsiasi await, serializza l'apertura proattiva:
// la prima callback che parte chiude la porta a tutte le altre. Si azzera da solo
// a ogni reload completo della pagina (nuovo contesto JS), il che va bene perché
// a quel punto il flag giornaliero in localStorage gestisce il "già aperto oggi".
let aperturaProattivaInFlight = false;

// Renderizza markdown essenziale: grassetto, corsivo, elenchi puntati
function renderMarkdown(testo: string) {
  const righe = testo.split('\n');
  return righe.map((riga, i) => {
    const isPuntato = /^[-•]\s/.test(riga);
    const contenuto = isPuntato ? riga.replace(/^[-•]\s/, '') : riga;
    const parti: React.ReactNode[] = [];
    const regex = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(contenuto)) !== null) {
      if (match.index > lastIndex) parti.push(contenuto.slice(lastIndex, match.index));
      if (match[2]) parti.push(<strong key={match.index}>{match[2]}</strong>);
      else if (match[3]) parti.push(<em key={match.index}>{match[3]}</em>);
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < contenuto.length) parti.push(contenuto.slice(lastIndex));
    if (isPuntato) return (
      <div key={i} style={{ display: 'flex', gap: 6, marginTop: 2 }}>
        <span style={{ marginTop: 1 }}>•</span><span>{parti}</span>
      </div>
    );
    if (contenuto.trim() === '') return <div key={i} style={{ height: 6 }} />;
    return <div key={i}>{parti}</div>;
  });
}

export default function CopilotaWidget({ userName }: { userName?: string }) {
  const [aperto, setAperto] = useState(false);
  const [messaggi, setMessaggi] = useState<Messaggio[]>([]);
  const [benvenutoInviato, setBenvenutoInviato] = useState(false);
  const [isTriggered, setIsTriggered] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [preparazioneProattiva, setPreparazioneProattiva] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Condiviso tra l'effetto del timer e qualunque interazione manuale
  // (anche il click sul FAB stesso): se true, l'apertura proattiva
  // in corso di preparazione viene scartata silenziosamente.
  const proattivoAnnullatoRef = useRef(false);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messaggi]);

  useEffect(() => {
    if (aperto) setTimeout(() => inputRef.current?.focus(), 100);
  }, [aperto]);

  // Continuità: all'avvio ricarica la coda della conversazione da copilota_logs,
  // così il thread sopravvive a chiusura/riapertura/reload. Mettiamo lo storico
  // DAVANTI a quello che è già in stato (prepend): se un'apertura proattiva è
  // già arrivata, resta in fondo, dopo la cronologia. Gira una volta sola.
  const storicoCaricatoRef = useRef(false);
  useEffect(() => {
    if (storicoCaricatoRef.current) return;
    storicoCaricatoRef.current = true;
    (async () => {
      try {
        const res = await fetch('/api/copilota/storico');
        const data = await res.json();
        const storico: Messaggio[] = Array.isArray(data?.messaggi) ? data.messaggi : [];
        if (storico.length > 0) {
          setMessaggi(prev => [...storico, ...prev]);
          setBenvenutoInviato(true); // c'è già un thread: niente benvenuto "da zero"
        }
      } catch {
        // Nessuno storico: si parte come prima (utente nuovo → benvenuto).
      }
    })();
  }, []);

  // Genera il messaggio di benvenuto dalla route (apertura MANUALE: il pannello
  // è già visibile, quindi qui va bene mostrare il loading dentro la chat)
  async function generaBenvenuto() {
    if (benvenutoInviato) return;
    setBenvenutoInviato(true);
    setLoading(true);
    try {
      const res = await fetch('/api/copilota', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messaggi: [{ role: 'user', content: '__apertura_automatica__' }],
          studioContext: null,
        }),
      });
      const data = await res.json();
      const testo = data.testo ?? 'Ciao! Come posso aiutarti?';
      setMessaggi(prev => [...prev, { ruolo: 'assistant', testo }]);
    } catch {
      setMessaggi(prev => [...prev, { ruolo: 'assistant', testo: 'Ciao! Come posso aiutarti?' }]);
    }
    setLoading(false);
  }

  // Proattività: prepara il messaggio di benvenuto IN SILENZIO (pannello chiuso,
  // solo un indicatore discreto sul FAB), e apre il pannello solo quando il
  // contenuto è pronto — niente pannello vuoto che carica a vista. Se l'utente
  // interagisce in qualunque momento prima che il pannello si apra (anche solo
  // un click, uno scroll, o l'apertura manuale), l'intera sequenza viene
  // annullata senza apparire mai.
  //
  // NIENTE gating su useAuth() qui — l'avevo aggiunto pensando che servisse
  // ad evitare richieste "alla cieca", ma è una premessa sbagliata: i cookie
  // di sessione del browser sono già lì, validi, indipendentemente da quanto
  // ci metta QUESTO componente a risolvere il proprio useAuth() (che peraltro
  // monta in ritardo, perché ConditionalAppShell aspetta un useEffect prima
  // di renderizzare il widget). Il controllo che conta è quello del server in
  // route.ts, sui cookie veri — aspettare qui non protegge nulla, aggiunge
  // solo ritardo e, con un tetto massimo, nuovi casi di mancato riconoscimento
  // proprio sulle sessioni valide più lente a risolversi lato client.
  useEffect(() => {
    const settings = localStorage.getItem('copilota_settings');
    const proattivo = settings ? JSON.parse(settings).proattivo !== false : true;
    if (!proattivo) return;

    const ultimaApertura = localStorage.getItem('copilota_ultima_apertura');
    const oggi = new Date().toDateString();
    if (ultimaApertura === oggi) return; // già proposto oggi

    proattivoAnnullatoRef.current = false;

    function segnalaInterazione() {
      proattivoAnnullatoRef.current = true;
    }

    function rimuoviListener() {
      window.removeEventListener('click', segnalaInterazione);
      window.removeEventListener('keydown', segnalaInterazione);
      window.removeEventListener('scroll', segnalaInterazione);
      window.removeEventListener('touchstart', segnalaInterazione);
    }

    window.addEventListener('click', segnalaInterazione);
    window.addEventListener('keydown', segnalaInterazione);
    window.addEventListener('scroll', segnalaInterazione);
    window.addEventListener('touchstart', segnalaInterazione);

    const timer = setTimeout(async () => {
      // Passato il breve ritardo, le interazioni "ambientali" sui listener
      // globali non contano più — ma NON marchiamo annullato solo per essere
      // arrivati qui: lo facciamo SOLO se proattivoAnnullatoRef.current era già true.
      rimuoviListener();
      if (proattivoAnnullatoRef.current) return;

      // Claim SINCRONO, prima di qualsiasi await: se un altro montaggio del widget
      // ha già avviato (o completato) l'apertura proattiva in questo tab, qui ci
      // fermiamo. Senza reset: una sola apertura per ciclo di vita della pagina.
      if (aperturaProattivaInFlight) return;
      aperturaProattivaInFlight = true;

      setPreparazioneProattiva(true);
      try {
        const res = await fetch('/api/copilota', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messaggi: [{ role: 'user', content: '__apertura_automatica__' }],
            studioContext: null,
          }),
        });
        if (proattivoAnnullatoRef.current) { setPreparazioneProattiva(false); return; } // interazione mentre aspettavamo la risposta
        const data = await res.json();
        const testo = data.testo ?? 'Ciao! Come posso aiutarti?';
        setMessaggi(prev => [...prev, { ruolo: 'assistant', testo }]);
        setBenvenutoInviato(true);
        setAperto(true);
        localStorage.setItem('copilota_ultima_apertura', oggi);
      } catch {
        if (proattivoAnnullatoRef.current) { setPreparazioneProattiva(false); return; }
        setMessaggi(prev => [...prev, { ruolo: 'assistant', testo: 'Ciao! Come posso aiutarti?' }]);
        setBenvenutoInviato(true);
        setAperto(true);
        localStorage.setItem('copilota_ultima_apertura', oggi);
      }
      setPreparazioneProattiva(false);
    }, 100);

    return () => {
      clearTimeout(timer);
      rimuoviListener();
    };
  }, []);

  // Se l'utente apre manualmente e non c'è ancora un messaggio — parte subito,
  // mostra l'indicatore di caricamento animato, nessuna attesa artificiale.
  useEffect(() => {
    if (aperto && messaggi.length === 0 && !benvenutoInviato && !isTriggered) {
      generaBenvenuto();
    }
  }, [aperto]);

  // Ascolta il trigger primo modulo completato dal QuizLoop
  useEffect(() => {
    const handlePrimoModulo = async () => {
      const settings = localStorage.getItem('copilota_settings');
      const proattivo = settings ? JSON.parse(settings).proattivo !== false : true;
      if (!proattivo) return;
      setIsTriggered(true);
      setBenvenutoInviato(true);
      setAperto(true);
      setLoading(true);
      try {
        const res = await fetch('/api/copilota', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messaggi: [{ role: 'user', content: '__primo_modulo_completato__' }],
            studioContext: null,
          }),
        });
        const data = await res.json();
        setMessaggi(prev => [...prev, { ruolo: 'assistant', testo: data.testo ?? 'Come è andata?' }]);
        setBenvenutoInviato(true);
      } catch {
        setMessaggi(prev => [...prev, { ruolo: 'assistant', testo: 'Come è andata con il primo modulo?' }]);
      }
      setLoading(false);
    };
    window.addEventListener('copilota:primo_modulo', handlePrimoModulo);
    return () => window.removeEventListener('copilota:primo_modulo', handlePrimoModulo);
  }, []);

  // Ascolta il trigger prima scheda completata dallo SchedeBrowser —
  // stesso schema di handlePrimoModulo, solo messaggio diverso.
  useEffect(() => {
    const handlePrimaScheda = async () => {
      const settings = localStorage.getItem('copilota_settings');
      const proattivo = settings ? JSON.parse(settings).proattivo !== false : true;
      if (!proattivo) return;
      setIsTriggered(true);
      setBenvenutoInviato(true);
      setAperto(true);
      setLoading(true);
      try {
        const res = await fetch('/api/copilota', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messaggi: [{ role: 'user', content: '__prima_scheda_completata__' }],
            studioContext: null,
          }),
        });
        const data = await res.json();
        setMessaggi(prev => [...prev, { ruolo: 'assistant', testo: data.testo ?? 'Come è andata?' }]);
        setBenvenutoInviato(true);
      } catch {
        setMessaggi(prev => [...prev, { ruolo: 'assistant', testo: 'Come è andata con la prima scheda?' }]);
      }
      setLoading(false);
    };
    window.addEventListener('copilota:prima_scheda', handlePrimaScheda);
    return () => window.removeEventListener('copilota:prima_scheda', handlePrimaScheda);
  }, []);

  // Avviso "novità Review": autosufficiente, controllato al mount del
  // widget stesso — niente evento esterno, zero rischio che un
  // CustomEvent sparato da un'altra pagina si perda se il listener non
  // è ancora attaccato. NON è Copilota proattivo (nessun controllo
  // 'proattivo', nessuna chiamata a /api/copilota): è un avviso fisso
  // di sviluppo, mostrato una sola volta per browser/dispositivo.
  useEffect(() => {
    if (localStorage.getItem('novita_review_vista')) return;
    (async () => {
      try {
        const res = await fetch('/api/archivista/feature-flags');
        const data = await res.json();
        if (!data.reviewEnabled) return; // non ancora abilitato per questo utente:
                                          // NON segniamo il flag, così lo vedrà
                                          // quando la feature sarà aperta a tutti
        localStorage.setItem('novita_review_vista', 'true');
        setIsTriggered(true);
        setBenvenutoInviato(true);
        setAperto(true);
        setMessaggi(prev => [...prev, {
          ruolo: 'assistant',
          testo: 'Novità: da oggi i tuoi errori non restano più legati al solo ripasso immediato — vengono ricordati, e trattati in modo diverso da persona a persona. Il tuo percorso con Moduli e Schede resta esattamente come era: nessun obbligo di cambiare nulla. La novità si chiama Review — una coda per recuperare quei quiz quando vuoi, in ordine di tua preferenza: Adattivo, Libero per materia, o Random. La card Review la trovi in home. Da qui in poi puoi comporre lo studio come ti serve: solo Schede, Schede e quiz insieme, Moduli con o senza recall subito dopo, Review quando e come vuoi, Simulazioni per misurarti sul serio.',
        }]);
      } catch {
        // Silenzioso: se il check fallisce, non mostriamo nulla e non
        // segniamo il flag — riproverà al prossimo mount
      }
    })();
  }, []);

  // Ascolta il trigger prima simulazione completata
  useEffect(() => {
    const handlePrimaSimulazione = async (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      const settings = localStorage.getItem('copilota_settings');
      const proattivo = settings ? JSON.parse(settings).proattivo !== false : true;
      if (!proattivo) return;
      setIsTriggered(true);
      setBenvenutoInviato(true);
      setAperto(true);
      setLoading(true);
      try {
        const res = await fetch('/api/copilota', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messaggi: [{ role: 'user', content: `__prima_simulazione_completata__:${JSON.stringify(detail)}` }],
            studioContext: null,
          }),
        });
        const data = await res.json();
        setMessaggi(prev => [...prev, { ruolo: 'assistant', testo: data.testo ?? 'Come è andata la simulazione?' }]);
      } catch {
        setMessaggi(prev => [...prev, { ruolo: 'assistant', testo: 'Come è andata la simulazione?' }]);
      }
      setLoading(false);
    };
    window.addEventListener('copilota:prima_simulazione', handlePrimaSimulazione);
    return () => window.removeEventListener('copilota:prima_simulazione', handlePrimaSimulazione);
  }, []);

  // Quando la chat è aperta, qualsiasi tasto digitato va nel campo input
  useEffect(() => {
    if (!aperto) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!inputRef.current) return;
      // Ignora tasti speciali, shortcuts e se il focus è già nell'input
      if (document.activeElement === inputRef.current) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key.length !== 1) return;
      inputRef.current.focus();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [aperto]);

  // Chiudere il pannello ora NON azzera la conversazione: il thread resta in
  // memoria e si ritrova riaprendo. Se il componente viene smontato (cambio
  // pagina), al rimontaggio lo storico si ricarica da copilota_logs.
  function chiudiPannello() {
    setAperto(false);
  }

  async function handleInvia() {
    const testo = input.trim();
    if (!testo || loading) return;

    const nuoviMessaggi: Messaggio[] = [...messaggi, { ruolo: 'user', testo }];
    setMessaggi(nuoviMessaggi);
    setInput('');
    setLoading(true);

    try {
      const storico = nuoviMessaggi.slice(-MAX_MESSAGGI);
      const res = await fetch('/api/copilota', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
        messaggi: storico.map(m => ({ role: m.ruolo, content: m.testo })),
        studioContext: window.location.pathname === '/' ? null : JSON.parse(localStorage.getItem('studio_context') ?? 'null'),
      }),
      });
      const data = await res.json();
      const risposta = data.testo ?? 'Non ho capito. Scrivi a info@3940.it.';
      setMessaggi(prev => [...prev, { ruolo: 'assistant', testo: risposta }]);
    } catch {
      setMessaggi(prev => [...prev, { ruolo: 'assistant', testo: 'Errore. Scrivi a info@3940.it.' }]);
    }

    setLoading(false);
  }

  return (
    <>
      {!aperto && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            proattivoAnnullatoRef.current = true; // interazione manuale: annulla sempre la sequenza proattiva
            setAperto(true);
          }}
          className={`copilota-fab ${preparazioneProattiva ? 'copilota-fab-preparazione' : ''}`}
          title="Copilota"
        >
          💬
        </button>
      )}

      {aperto && (
        <>
          <div onClick={chiudiPannello} className="copilota-backdrop" />
          <div onClick={(e) => e.stopPropagation()} className="copilota-panel">
            <div className="copilota-header">
              <span>Copilota 3940</span>
              <button
                onClick={(e) => { e.stopPropagation(); chiudiPannello(); }}
                className="copilota-close"
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <div className="copilota-messaggi">
              {messaggi.map((m, i) => (
                <div
                  key={i}
                  className="copilota-bolla"
                  style={{
                    alignSelf: m.ruolo === 'user' ? 'flex-end' : 'flex-start',
                    backgroundColor: m.ruolo === 'user' ? '#f97316' : '#f1f5f9',
                    color: m.ruolo === 'user' ? 'white' : '#1e293b',
                  }}
                >
                  {m.ruolo === 'assistant' ? renderMarkdown(m.testo) : m.testo}
                </div>
              ))}
              {loading && (
                <div className="copilota-bolla" style={{ alignSelf: 'flex-start', backgroundColor: '#f1f5f9' }}>
                  <div className="copilota-typing">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            <div className="copilota-input-row">
              <input
                ref={inputRef}
                type="text"
                placeholder="Scrivi un messaggio..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') handleInvia(); }}
                disabled={loading}
                className="copilota-input"
              />
              <button
                onClick={(e) => { e.stopPropagation(); handleInvia(); }}
                disabled={loading || !input.trim()}
                className="copilota-send"
              >
                →
              </button>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        .copilota-fab {
          position: fixed;
          bottom: 24px;
          right: 24px;
          z-index: 9001;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background-color: #f97316;
          color: white;
          border: none;
          cursor: pointer;
          font-size: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
        }

        .copilota-fab-preparazione {
          animation: copilota-pulse 1.2s ease-in-out infinite;
        }

        @keyframes copilota-pulse {
          0%, 100% { box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15), 0 0 0 0 rgba(249, 115, 22, 0.5); }
          50% { box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15), 0 0 0 8px rgba(249, 115, 22, 0); }
        }

        .copilota-backdrop {
          position: fixed;
          inset: 0;
          z-index: 8999;
        }

        .copilota-panel {
          position: fixed;
          bottom: 24px;
          right: 24px;
          z-index: 9000;
          width: min(560px, calc(50vw - 32px));
          height: min(640px, calc(100vh - 100px));
          background-color: white;
          border-radius: 16px;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .copilota-header {
          background-color: #f97316;
          color: white;
          padding: 12px 16px;
          font-size: 14px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .copilota-close {
          background: transparent;
          border: none;
          color: white;
          font-size: 22px;
          line-height: 1;
          cursor: pointer;
          padding: 0 4px;
        }

        .copilota-messaggi {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .copilota-bolla {
          border-radius: 12px;
          padding: 8px 12px;
          font-size: 13px;
          max-width: 85%;
          line-height: 1.4;
        }

        .copilota-typing {
          display: flex;
          gap: 4px;
          padding: 2px 0;
        }

        .copilota-typing span {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background-color: #94a3b8;
          animation: copilota-typing-bounce 1.4s ease-in-out infinite;
        }

        .copilota-typing span:nth-child(2) { animation-delay: 0.2s; }
        .copilota-typing span:nth-child(3) { animation-delay: 0.4s; }

        @keyframes copilota-typing-bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }

        .copilota-input-row {
          border-top: 1px solid #e2e8f0;
          padding: 8px 12px;
          padding-bottom: max(8px, env(safe-area-inset-bottom));
          display: flex;
          gap: 8px;
        }

        .copilota-input {
          flex: 1;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 6px 10px;
          font-size: 13px;
          outline: none;
        }

        .copilota-send {
          background-color: #f97316;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 6px 12px;
          font-size: 13px;
          cursor: pointer;
        }

        .copilota-send:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        /* --- Mobile: pannello a schermo intero --- */
        @media (max-width: 768px) {
          .copilota-panel {
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            width: 100vw;
            height: 100dvh;
            border-radius: 0;
          }

          .copilota-header {
            padding-top: max(12px, env(safe-area-inset-top));
          }

          .copilota-bolla {
            max-width: 90%;
            font-size: 14px;
          }

          .copilota-fab {
            bottom: max(24px, env(safe-area-inset-bottom));
          }
        }
      `}</style>
    </>
  );
}